// Who is signed in and which business they are working on. People can belong to several businesses;
// the one they chose is remembered on this device and sent with every API call (x-tenant-id).
import { createContext, type ReactNode, useContext } from 'react';

export type Role = 'owner' | 'manager' | 'staff';

export interface BusinessLocale {
  country: string;
  currency: string;
  formatLocale: string;
  timeZone: string;
  languages: string[];
}

export interface Business {
  id: string;
  name: string;
  slug: string;
  role: Role;
  locale: BusinessLocale;
  /** True once the owner finished (or skipped) onboarding. */
  onboarded: boolean;
}

export interface Me {
  user: { id: string; name: string; email: string; image: string | null };
  businesses: Business[];
  /** The platform owner: may change the country rule packs. */
  platformAdmin?: boolean;
  /** Platform staff viewing a business read-only ("view as"). */
  viewingAs?: { businessId: string; by: string } | null;
}

const KEY = 'qbl.business';
let current: string | null = null;
try {
  current = localStorage.getItem(KEY);
} catch {
  current = null;
}

export function currentBusinessId(): string | null {
  return current;
}

export function chooseBusiness(id: string | null): void {
  current = id;
  try {
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
  } catch {
    // Not remembered on this device; still used for this visit.
  }
}

const BusinessContext = createContext<Business | null>(null);
const MeContext = createContext<Me | null>(null);

export function SessionProvider({
  me,
  business,
  children,
}: {
  me: Me | null;
  business: Business | null;
  children: ReactNode;
}) {
  return (
    <MeContext.Provider value={me}>
      <BusinessContext.Provider value={business}>{children}</BusinessContext.Provider>
    </MeContext.Provider>
  );
}

export function useBusiness(): Business | null {
  return useContext(BusinessContext);
}

export function useMe(): Me | null {
  return useContext(MeContext);
}

/** What the current person may do here (mirrors the API's own checks; the API always decides). */
export function can(
  business: Business | null,
  action: 'manage' | 'billing' | 'team' | 'restricted',
): boolean {
  if (!business) return false;
  if (business.role === 'owner') return true;
  if (business.role === 'manager') return action !== 'billing';
  return false;
}
